import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";

import { XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import AutopilotCuratedSearchContext from "../../contexts/AutopilotCuratedSearchContext";
import { useContext, useState } from "react";
import CiroTextInput from "../shared/CiroTextInput";
import { getScoringCriteriaErrors } from "./curatedSearchUtils";
import { toast } from "react-hot-toast";
import { ScaleIcon } from "@heroicons/react/24/outline";
import CiroExpandableCard from "../shared/CiroExpandableCard";
import CuratedSearchCardHeader from "./CuratedSearchCard/CuratedSearchCardHeader";
import { HeaderPill } from "./CuratedSearchFiltersHeaderDisplay";

const CuratedSearchScoringCriteriaContainer = ({
  generateScoringCriteriaLoading,
  showPlanningStrategy,
  setShowPlanningStrategy,
  isOpen,
  setIsOpen,
}: {
  generateScoringCriteriaLoading: boolean;
  showPlanningStrategy: boolean;
  setShowPlanningStrategy: (value: boolean) => void;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}) => {
  const { scoringCriteriaValues, setScoringCriteriaValues } = useContext(
    AutopilotCuratedSearchContext,
  );

  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const scoringCriteriaErrors = getScoringCriteriaErrors(scoringCriteriaValues);

  return (
    <CiroExpandableCard
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <CuratedSearchCardHeader
          headerText={`Scoring criteria${
            isOpen ? "" : ` (${scoringCriteriaValues.length})`
          }`}
          headerIcon={<ScaleIcon className="h-6 w-6 text-orange-500" />}
        />
      }
      collapsedContent={
        <div
          className={classNames(
            "text-sm",
            "text-zinc-500",
            "whitespace-nowrap",
            "[mask-image:linear-gradient(to_right,black_75%,transparent)]",
          )}
        >
          <span className={classNames("flex", "gap-2", "items-center")}>
            <span className={classNames("text-xs")}>Evaluate based on</span>
            {scoringCriteriaValues.map((value, idx) => (
              <HeaderPill
                key={`${value}-${idx}`}
                text={value.length > 20 ? `${value.slice(0, 20)}...` : value}
              />
            ))}
          </span>
        </div>
      }
    >
      <div>
        {generateScoringCriteriaLoading && (
          <div className={classNames("text-sm", "text-zinc-500", "pb-4")}>
            Generating scoring criteria...
          </div>
        )}
        {!generateScoringCriteriaLoading && (
          <div className={classNames("flex", "flex-col", "gap-2")}>
            <div className={classNames("italic", "text-sm", "text-zinc-500")}>
              AI-generated criteria to help score profiles. Feel free to edit
              them!
            </div>
            {scoringCriteriaValues.map((filter, idx) => (
              <div
                key={`filter-container-${idx}`}
                className={classNames(
                  "flex",
                  "items-center",
                  "gap-4",
                  "px-1",
                  "text-sm",
                )}
              >
                <CiroTextInput
                  key={idx}
                  wrapperClassName={classNames("w-full")}
                  value={filter}
                  error={
                    showValidationErrors ? scoringCriteriaErrors[idx] : null
                  }
                  onChange={(e) => {
                    const newScoringCriteriaValues = [...scoringCriteriaValues];
                    newScoringCriteriaValues[idx] = e.target.value;
                    setScoringCriteriaValues(newScoringCriteriaValues);

                    // Clear validation errors if all criteria are valid after editing
                    if (showValidationErrors) {
                      const newErrors = getScoringCriteriaErrors(
                        newScoringCriteriaValues,
                      );

                      if (!newErrors.some((error) => error !== null)) {
                        setShowValidationErrors(false);
                      }
                    }
                  }}
                />
                <CiroButton
                  analyticsField={"Removing scoring criteria"}
                  style={CiroButtonStyleEnum.UNSTYLED}
                  onClick={() => {
                    const newScoringCriteriaValues = [...scoringCriteriaValues];
                    newScoringCriteriaValues.splice(idx, 1);
                    setScoringCriteriaValues(newScoringCriteriaValues);
                  }}
                >
                  <XMarkIcon
                    className={classNames("w-5", "text-neutral-500")}
                  />
                </CiroButton>
              </div>
            ))}

            <div
              className={classNames(
                "mt-2",
                "ml-1",
                "w-full",
                "flex",
                "justify-between",
              )}
            >
              <CiroButton
                analyticsField={"Add scoring criteria"}
                style={CiroButtonStyleEnum.PRIMARY}
                onClick={() => {
                  const newScoringCriteriaValues = [
                    ...scoringCriteriaValues,
                    "",
                  ];
                  setScoringCriteriaValues(newScoringCriteriaValues);
                }}
              >
                <div className={classNames("flex", "items-center", "gap-2")}>
                  <span>+</span>
                  <span>Add criterion</span>
                </div>
              </CiroButton>
              {!showPlanningStrategy && (
                <div className={classNames("pr-2")}>
                  <CiroButton
                    analyticsField={"Continue to search strategy"}
                    style={CiroButtonStyleEnum.LOUD}
                    onClick={() => {
                      if (
                        scoringCriteriaErrors.some((error) => error !== null)
                      ) {
                        setShowValidationErrors(true);
                        toast.error("Please fix errors in scoring criteria");
                      } else {
                        setShowPlanningStrategy(true);
                        setIsOpen(false);
                      }
                    }}
                  >
                    Continue
                  </CiroButton>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CiroExpandableCard>
  );
};

export default CuratedSearchScoringCriteriaContainer;
